import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Typography from "../../../../components/Typography/Typography";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { Button } from "../../../../components/Buttons";
import IconComponent from "../../../../components/Icon/IconComponent";
import { postRequest, getRequest } from "../../../../Apis/apiWrapper";
import { paymentEndpoints } from "../../../../utils/constants/httpConstants";
import { NO_RECORD_FOUND_IMAGE } from "../../../../utils/constants/assets";
import {
  BRAND,
  GREY,
  BASE,
  PRIMARY,
  BORDER,
  SUCCESS,
} from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import { EMITTER_ERROR } from "../../../../utils/constants";
import EventEmitter from "../../../../utils/emitter/EventEmitter";

const BuyPackage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingPlanId, setBuyingPlanId] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  const styles = {
    container: {
      margin: "16px 0px 0px 16px",
      borderRadius: "6px",
      height: "100%",
    },
    mainWrapper: {
      background: BASE.WHITE,
      height: "100%",
      overflow: "auto",
      borderRadius: "6px",
    },
    header: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      borderBottom: `1px solid ${BORDER.DEFAULT}`,
      width: "100%",
    },
    contentWrapper: {
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "24px",
    },
    plansGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: "24px",
    },
    planCard: {
      background: BASE.WHITE,
      border: `1px solid ${BORDER.DEFAULT}`,
      borderRadius: "12px",
      padding: "32px 24px",
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "visible",
    },
    planCardFeatured: {
      background: BASE.WHITE,
      border: `2px solid ${BRAND.PRIMARY}`,
      borderRadius: "12px",
      padding: "32px 24px",
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "visible",
      transform: "scale(1.05)",
      boxShadow: `0 12px 32px rgba(255, 72, 72, 0.2)`,
    },
    planCardCurrent: {
      background: PRIMARY.W_50,
      border: `2px solid ${BRAND.PRIMARY}`,
      borderRadius: "12px",
      padding: "32px 24px",
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "visible",
    },
    featuredBadge: {
      position: "absolute",
      top: "-12px",
      left: "50%",
      transform: "translateX(-50%)",
      background: BRAND.PRIMARY,
      color: BASE.WHITE,
      padding: "6px 20px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: fontWeight.BOLD,
      boxShadow: `0 4px 12px rgba(255, 72, 72, 0.3)`,
    },
    currentBadge: {
      position: "absolute",
      top: "16px",
      right: "16px",
      background: SUCCESS.W_500,
      color: BASE.WHITE,
      padding: "4px 12px",
      borderRadius: "16px",
      fontSize: "11px",
      fontWeight: fontWeight.SEMIBOLD,
    },
    planHeader: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      paddingBottom: "16px",
      borderBottom: `1px solid ${BORDER.DEFAULT}`,
    },
    planName: {
      fontSize: "24px",
      fontWeight: fontWeight.BOLD,
      color: GREY.PRIMARY,
    },
    planDescription: {
      fontSize: "14px",
      fontWeight: fontWeight.REGULAR,
      color: GREY.SECONDARY,
      lineHeight: "1.5",
    },
    priceWrapper: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    priceContainer: {
      display: "flex",
      alignItems: "baseline",
      gap: "8px",
    },
    price: {
      fontSize: "42px",
      fontWeight: fontWeight.BOLD,
      color: BRAND.PRIMARY,
    },
    priceLabel: {
      fontSize: "16px",
      fontWeight: fontWeight.REGULAR,
      color: GREY.SECONDARY,
    },
    creditsLabel: {
      fontSize: "14px",
      fontWeight: fontWeight.SEMIBOLD,
      color: BRAND.PRIMARY,
    },
    featuresList: {
      display: "flex",
      flexDirection: "column",
      gap: "14px",
    },
    featureItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
    },
    checkIcon: {
      fontSize: "20px",
      color: SUCCESS.W_500,
      marginTop: "2px",
    },
    featureText: {
      fontSize: "14px",
      fontWeight: fontWeight.REGULAR,
      color: GREY.SECONDARY,
      lineHeight: "1.6",
    },
    buttonWrapper: {
      marginTop: "auto",
    },
    comparisonNote: {
      background: PRIMARY.W_50,
      padding: "16px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    infoIcon: {
      fontSize: "20px",
      color: BRAND.PRIMARY,
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "400px",
    },
    emptyStateContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
      gap: "16px",
      padding: "40px",
    },
    emptyIcon: {
      fontSize: "80px",
      color: GREY.TERTIARY,
    },
    emptyTitle: {
      fontSize: "20px",
      fontWeight: fontWeight.SEMIBOLD,
      color: GREY.PRIMARY,
    },
    emptyDescription: {
      fontSize: "14px",
      fontWeight: fontWeight.REGULAR,
      color: GREY.SECONDARY,
      textAlign: "center",
      maxWidth: "400px",
    },
  };

  useEffect(() => {
    fetchProducts();
    fetchCurrentSubscription();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await postRequest(
        navigate,
        paymentEndpoints.listProducts,
        {
          limit: 10,
        },
      );

      if (response?.data?.status && response?.data?.data?.packages) {
        const packagesData = response.data.data.packages;

        if (packagesData.length === 0) {
          setPackages([]);
          return;
        }

        const formattedPackages = packagesData.map((pkg, index) => {
          // Determine if it should be featured based on name or index
          const isFeatured =
            pkg.name?.toLowerCase().includes("business") || index === 1;

          return {
            id: pkg._id,
            name: pkg.name || `Package ${index + 1}`,
            description:
              pkg.description || "Perfect for growing teams and businesses",
            price: pkg.amount || 0,
            currency: pkg.currency?.toUpperCase() || "USD",
            features: [
              "Email verification",
              "Priority support",
              "Export to CSV & Excel",
              "Advanced API access",
              "Custom integrations",
            ],
            featured: isFeatured,
            interval: pkg.billing_period || "month",
            stripe_price_id: pkg.stripe_price_id,
            credits: pkg.credits || 0,
          };
        });

        setPackages(formattedPackages);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to load packages",
      );
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await getRequest(
        navigate,
        paymentEndpoints.currentSubscription
      );
      if (response?.data?.status && response?.data?.data?.has_subscription) {
        setCurrentSubscription(response.data.data.subscription);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  const handleBuyPackage = async (plan) => {
    if (!plan.stripe_price_id) {
      EventEmitter.emit(EMITTER_ERROR, "This package is not available for purchase.");
      return;
    }

    try {
      setBuyingPlanId(plan.id);
      const response = await postRequest(
        navigate,
        paymentEndpoints.createCheckoutSession,
        { price_id: plan.stripe_price_id }
      );

      if (response?.data?.status && response?.data?.data?.checkout_url) {
        window.location.href = response.data.data.checkout_url;
      } else {
        EventEmitter.emit(
          EMITTER_ERROR,
          response?.data?.message || "Failed to create checkout session."
        );
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to initiate payment. Please try again."
      );
    } finally {
      setBuyingPlanId(null);
    }
  };

  if (loading) {
    return (
      <Box style={styles.container}>
        <Box style={styles.mainWrapper}>
          <Box style={styles.header}>
            <Typography
              size={"md"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.MEDIUM}
              label="Buy Package"
            />
          </Box>
          <Box style={styles.loadingContainer}>
            <Typography
              size={"md"}
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
              label="Loading available packages..."
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if (packages.length === 0) {
    return (
      <Box style={styles.container}>
        <Box style={styles.mainWrapper}>
          <Box style={styles.header}>
            <Typography
              size={"md"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.MEDIUM}
              label="Buy Package"
            />
            <TypographyInline
              size={"sm"}
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
              label="Choose the perfect plan for your business needs"
            />
          </Box>
          <Box style={styles.emptyStateContainer}>
            <img
              src={NO_RECORD_FOUND_IMAGE}
              alt="Not Found"
              style={{ width: "200px", height: "auto" }}
            />
            <span style={styles.emptyTitle}>No Packages Found</span>
            <span style={styles.emptyDescription}>
              We couldn't find any packages at the moment. Please check back
              later.
            </span>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box style={styles.container}>
      <Box style={styles.mainWrapper}>
        <Box style={styles.header}>
          <Typography
            size={"md"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label="Buy Package"
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label="Choose the perfect plan for your business needs"
          />
        </Box>

        <Box style={styles.contentWrapper}>
          {/* Comparison Note */}
          <Box style={styles.comparisonNote}>
            <IconComponent
              iconLabel={ICON_LABELS.INFO}
              style={styles.infoIcon}
            />
            <TypographyInline
              size={"sm"}
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
              label="All plans include email verification, data export, and regular updates. Upgrade or downgrade anytime."
            />
          </Box>

          {/* Plans Grid */}
          <Box style={styles.plansGrid}>
            {packages.map((plan) => {
              const isCurrent =
                currentSubscription &&
                currentSubscription.plan?.id === plan.stripe_price_id &&
                currentSubscription.status === "active";
              const isSelected = selectedPlan === plan.id;

              let cardStyle = styles.planCard;
              if (plan.featured) {
                cardStyle = styles.planCardFeatured;
              } else if (isCurrent) {
                cardStyle = styles.planCardCurrent;
              }

              return (
                <Box
                  key={plan.id}
                  style={cardStyle}
                  onClick={() => setSelectedPlan(plan.id)}
                  onMouseEnter={(e) => {
                    if (!plan.featured && !isCurrent) {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow =
                        "0 20px 40px rgba(0, 0, 0, 0.08)";
                      e.currentTarget.style.borderColor = BRAND.PRIMARY;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!plan.featured && !isCurrent) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.borderColor = BORDER.DEFAULT;
                    }
                  }}
                >
                  {plan.featured && (
                    <Box style={styles.featuredBadge}>MOST POPULAR</Box>
                  )}
                  {isCurrent && (
                    <Box style={styles.currentBadge}>SUBSCRIBED</Box>
                  )}

                  <Box style={styles.planHeader}>
                    <Box style={styles.planName}>{plan.name}</Box>
                    <Box style={styles.planDescription}>{plan.description}</Box>
                  </Box>

                  <Box style={styles.priceWrapper}>
                    <Box style={styles.priceContainer}>
                      <span style={styles.price}>
                        {plan.currency === "USD" ? "$" : plan.currency}
                        {plan.price}
                      </span>
                      <span style={styles.priceLabel}>/{plan.interval}</span>
                    </Box>
                    {plan.credits > 0 && (
                      <span style={styles.creditsLabel}>
                        {plan.credits.toLocaleString()} credits
                      </span>
                    )}
                  </Box>

                  <Box style={styles.featuresList}>
                    {plan.features.map((feature, index) => (
                      <Box key={index} style={styles.featureItem}>
                        <IconComponent
                          iconLabel={ICON_LABELS.CHECK_CIRCLE}
                          style={styles.checkIcon}
                        />
                        <span style={styles.featureText}>{feature}</span>
                      </Box>
                    ))}
                  </Box>

                  <Box style={styles.buttonWrapper}>
                    <Button
                      label={
                        buyingPlanId === plan.id
                          ? "Redirecting..."
                          : isCurrent
                            ? "Subscribed"
                            : isSelected
                              ? "Get Started"
                              : "Select Plan"
                      }
                      size={"md40"}
                      fullWidth
                      variant={
                        isCurrent || isSelected || plan.featured
                          ? "primary"
                          : "ghost"
                      }
                      disabled={isCurrent || buyingPlanId !== null}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyPackage(plan);
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BuyPackage;
